import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, Loader2, AlertTriangle, Trophy, Wrench } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const depthColor = (n) => (n >= 80 ? "#10b981" : n >= 60 ? "#38bdf8" : n >= 40 ? "#f59e0b" : "#f43f5e");

// Genre Benchmarks — scores every game TYPE in the creator's library by real Creation Depth + avg
// Playtest (+ Visual QA), weakest first. One tap on "Improve" jumps the Compiler straight into
// building the weakest game of that genre (Weakest-First Build).
export default function GenreBenchmarks() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/compiler/benchmarks`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Couldn't load genre benchmarks."))
      .finally(() => setLoading(false));
  }, [open]);

  const improve = (r) => {
    if (!r.sample_game_id) { toast.error("No game to improve for this type yet."); return; }
    setOpen(false);
    toast.message(`Jumping into your weakest ${r.label} to build it up…`);
    navigate(`/app/compiler/${r.sample_game_id}?autobuild=1`);
  };

  const genres = data?.genres || [];
  const weakest = new Set(data?.weakest || []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full gap-1.5 h-7 px-3 text-xs" data-testid="compiler-benchmarks-btn">
          <BarChart3 className="w-3.5 h-3.5" /> Benchmarks
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[88vh] overflow-y-auto" data-testid="compiler-benchmarks-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-[#38bdf8]" /> Genre Benchmarks</DialogTitle></DialogHeader>

        {loading ? (
          <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--muted-foreground))]" /></div>
        ) : genres.length === 0 ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))] leading-snug py-4" data-testid="compiler-benchmarks-empty">
            No games to benchmark yet. Build a few games across different types and this board will show where your Compiler shines and where it needs work.
          </p>
        ) : (
          <>
            <p className="text-[12px] text-[hsl(var(--muted-foreground))] leading-snug" data-testid="compiler-benchmarks-summary">
              Average Creation Depth across <b>{data.total_games}</b> games in <b>{data.types}</b> game types — weakest first. Focus here to level up your Compiler.
            </p>
            <div className="space-y-2 mt-1" data-testid="compiler-benchmarks-list">
              {genres.map((r, i) => (
                <div key={r.template} className={`rounded-lg border px-3 py-2 ${weakest.has(r.template) ? "border-rose-500/40 bg-rose-500/5" : "border-[hsl(var(--border))]"}`} data-testid={`compiler-benchmark-${r.template}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {i === 0 && genres.length > 2 && <AlertTriangle className="w-3.5 h-3.5 text-rose-500 shrink-0" />}
                    {i === genres.length - 1 && genres.length > 2 && r.avg_depth >= 70 && <Trophy className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                    <span className="text-sm font-medium flex-1 truncate">{r.label}</span>
                    <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{r.games} game{r.games === 1 ? "" : "s"}</span>
                    {r.avg_playtest != null && <span className="text-[10px] font-semibold text-emerald-500" title="avg playtest score">🎮 {r.avg_playtest}</span>}
                    {r.avg_visual != null && <span className="text-[10px] font-semibold text-[#a855f7]" title="avg visual score">👁 {r.avg_visual}</span>}
                    <span className="text-sm font-extrabold tabular-nums w-9 text-right" style={{ color: depthColor(r.avg_depth) }}>{r.avg_depth}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-2 rounded-full bg-[hsl(var(--secondary))] overflow-hidden flex-1">
                      <div className="h-full rounded-full transition-all" style={{ width: `${r.avg_depth}%`, background: depthColor(r.avg_depth) }} />
                    </div>
                    {r.sample_game_id && (
                      <button onClick={() => improve(r)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--primary))] hover:underline shrink-0" data-testid={`compiler-benchmark-improve-${r.template}`}>
                        <Wrench className="w-3 h-3" /> Improve
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Number = avg depth (0–100). 🎮 avg playtest · 👁 avg visual (where scanned). "Improve" opens your weakest game of that type and starts building.</p>
          </>
        )}
        <button onClick={() => { setOpen(false); navigate("/leaderboard"); }} className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[hsl(var(--primary))] hover:underline" data-testid="compiler-benchmarks-leaderboard">
          <Trophy className="w-3.5 h-3.5 text-amber-400" /> Compare with the community leaderboard →
        </button>
      </DialogContent>
    </Dialog>
  );
}
